import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Container, Typography, TextField, IconButton, Button,
  Box, Chip, CircularProgress, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search, CameraAlt, Close, Edit,
  FlashOn, FlashOff, Delete, OpenInNew, CheckCircle, Add,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Quagga from 'quagga';
import QuantityFoodDialog from '../Components/QuantityFoodDialog';
import FoodFormDialog from '../Components/FoodFormDialog';
import DishLoggingDialog from '../Components/DishLoggingDialog';
import SuccessOverlay, { isAnimEnabled } from '../Components/SuccessOverlay';
import { PageHeader, ListRow } from '../Components/ui';
import { radius } from '../theme';
import { MEAL_LABELS } from '../constants/mealLabels';

// ─── Konstanten ───────────────────────────────────────────────────────────────

const DETECTION_CONFIRMATION_COUNT = 3;
const MAX_DECODE_ERROR = 0.08;
const MIN_VALID_SEGMENTS = 4;
const CODE_VALIDATION_REGEX = /^(\d{8}|\d{12}|\d{13})$/;
const TORCH_TRACK_STORE_KEY = 'nobnohungry-torch-track-id';

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

const isValidEAN = (code) => {
  if (!CODE_VALIDATION_REGEX.test(code)) return false;
  const digits = code.split('').map(Number);
  if (digits.some((d) => Number.isNaN(d))) return false;

  if (digits.length === 8) {
    const sum = digits.slice(0, 7).reduce((a, d, i) => a + d * (i % 2 === 0 ? 3 : 1), 0);
    return (10 - (sum % 10)) % 10 === digits[7];
  }
  if (digits.length === 12) {
    const sum = digits.slice(0, 11).reduce((a, d, i) => a + d * (i % 2 === 0 ? 3 : 1), 0);
    return (10 - (sum % 10)) % 10 === digits[11];
  }
  if (digits.length === 13) {
    const sum = digits.slice(0, 12).reduce((a, d, i) => a + d * (i % 2 === 0 ? 1 : 3), 0);
    return (10 - (sum % 10)) % 10 === digits[12];
  }
  return false;
};

const extractPossibleEANs = (value) => {
  if (!value) return [];
  const matches = value.match(/\d{8,13}/g) || [];
  const seen = new Set();
  matches.forEach((c) => { if (isValidEAN(c)) seen.add(c); });
  return Array.from(seen);
};

const toRoundedNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : null;
};

const formatDate = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const todayLocal = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};


// ─── Hauptkomponente ──────────────────────────────────────────────────────────

const MealPage = ({ mealtime, title, accentColor }) => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const location  = useLocation();
  const dateParam = new URLSearchParams(location.search).get('date');
  const [selectedDate, setSelectedDate] = useState(dateParam || todayLocal());

  // ── State ──────────────────────────────────────────────────────────────────
  const [searchText,              setSearchText]             = useState('');
  const [foodItems,               setFoodItems]              = useState([]);
  const [selectedItem,            setSelectedItem]           = useState(null);
  const [quantity,                setQuantity]               = useState(1);
  const [unit,                    setUnit]                   = useState('100');
  const [totalCalories,           setTotalCalories]          = useState(0);
  const [snackbarMessage,         setSnackbarMessage]        = useState('');
  const [isDialogOpen,            setDialogOpen]             = useState(false);
  const [isOwnDialogOpen,         setOwnDialogOpen]          = useState(false);
  const [tabIndex,                setTabIndex]               = useState(0);
  const [isSearching,             setIsSearching]            = useState(false);
  const [isScanning,              setIsScanning]             = useState(false);
  const [lastAddedItems,          setLastAddedItems]         = useState([]);
  const [ownAddedItems,           setOwnAddedItems]          = useState([]);
  const [filteredLastAddedItems,  setFilteredLastAddedItems] = useState([]);
  const [filteredOwnAddedItems,   setFilteredOwnAddedItems]  = useState([]);
  const [crossMealItems,          setCrossMealItems]         = useState([]);
  const [editDialogOpen,          setEditDialogOpen]         = useState(false);
  const [addDialogOpen,           setAddDialogOpen]          = useState(false);
  const [dishDialogItem,          setDishDialogItem]         = useState(null);
  const [dishSuggestions,         setDishSuggestions]        = useState([]);
  const [isFromSearch,            setIsFromSearch]           = useState(false);
  const [databaseSource,          setDatabaseSource]         = useState('openfoodfacts');
  const [pendingBarcode,          setPendingBarcode]         = useState('');
  const [detectionConfidence,     setDetectionConfidence]    = useState(0);
  const [isTorchOn,               setIsTorchOn]              = useState(false);
  const [isTorchAvailable,        setIsTorchAvailable]       = useState(false);
  const [scanSuccess,             setScanSuccess]            = useState(false);

  // Erfolgs-Overlay
  const [showSuccess,   setShowSuccess]   = useState(false);
  const [savedEntry,    setSavedEntry]    = useState(null);
  const [calorieGoal,   setCalorieGoal]   = useState(null);
  const [consumedToday, setConsumedToday] = useState(0);

  const handleSearchRef        = useRef(null);
  const lastDetectedCodeRef    = useRef('');
  const consecutiveDetections  = useRef(0);
  const activeTrackRef         = useRef(null);
  const scanLockedRef          = useRef(false);

  // Kalorienstand für die "Vorher/Nachher"-Animation laden
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    Promise.all([
      fetch('/api/users/get-settings', { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.ok ? r.json() : null),
      fetch('/api/users/get-calories', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ date: selectedDate }) }).then((r) => r.ok ? r.json() : null),
    ]).then(([settings, calories]) => {
      if (settings?.calories) setCalorieGoal(settings.calories);
      if (calories?.total_calories !== undefined) setConsumedToday(Number(calories.total_calories) || 0);
    }).catch(console.error);
  }, [selectedDate]);

  // ── Erfolgs-Overlay ────────────────────────────────────────────────────────
  const triggerSuccess = (name, kcal) => {
    const prevRemaining = calorieGoal !== null ? calorieGoal - consumedToday : null;
    const newRemaining  = prevRemaining !== null ? prevRemaining - kcal : null;
    setSavedEntry({ name, kcal, prevRemaining, newRemaining });
    setConsumedToday((prev) => prev + kcal);
    setShowSuccess(true);
    const duration = isAnimEnabled() ? (prevRemaining !== null ? 2800 : 1700) : 300;
    setTimeout(() => setShowSuccess(false), duration);
  };

  // ── Kalorien & Makros berechnen ────────────────────────────────────────────
  const [totalMacros, setTotalMacros] = useState({ protein: 0, fat: 0, carbs: 0 });

  useEffect(() => {
    if (!selectedItem) return;

    const q = Number.isFinite(quantity) ? quantity : 0;

    // Kalorien
    if (typeof selectedItem.kcal_complete === 'number') {
      setTotalCalories(Math.round(selectedItem.kcal_complete * quantity));
      setTotalMacros({
        protein: Math.round((Number(selectedItem.protein_g) || 0) * quantity),
        fat:     Math.round((Number(selectedItem.fat_g)     || 0) * quantity),
        carbs:   Math.round((Number(selectedItem.carbs_g)   || 0) * quantity),
      });
      return;
    }

    const perHundred =
      typeof selectedItem.kcal === 'number'          ? selectedItem.kcal :
      typeof selectedItem.kcal_per_100g === 'number' ? selectedItem.kcal_per_100g : null;
    const perPortion =
      typeof selectedItem.kcal_serving === 'number' ? selectedItem.kcal_serving : null;

    let cal = 0;
    if ((unit === 'g' || unit === 'ml') && perHundred !== null)   cal = (perHundred / 100) * q;
    else if (unit === 'portion' && perPortion !== null)            cal = perPortion * q;
    else if (unit === '100' && perHundred !== null)                cal = perHundred * q;
    else if (perPortion !== null)                                  cal = perPortion * q;
    else if (perHundred !== null)                                  cal = perHundred * q;
    setTotalCalories(Math.round(cal));

    // Makros (immer per 100g auf dem Item)
    const calcMacro = (per100) => {
      if (per100 === null || per100 === undefined) return 0;
      if ((unit === 'g' || unit === 'ml')) return Math.round((per100 / 100) * q);
      if (unit === 'portion')              return Math.round((per100 / 100) * (selectedItem.portion_size_g || 100));
      return Math.round(per100 * q); // unit '100'
    };
    setTotalMacros({
      protein: calcMacro(selectedItem.protein_100g ?? selectedItem.protein_g ?? null),
      fat:     calcMacro(selectedItem.fat_100g     ?? selectedItem.fat_g     ?? null),
      carbs:   calcMacro(selectedItem.carbs_100g   ?? selectedItem.carbs_g   ?? null),
    });
  }, [quantity, unit, selectedItem]);

  // ── Torch ──────────────────────────────────────────────────────────────────
  const applyTorchToTrack = async (track, enabled) => {
    if (!track || typeof track.getCapabilities !== 'function' || typeof track.applyConstraints !== 'function') return false;
    const cap = track.getCapabilities();
    const torchOk = cap?.torch === true;
    const fillOk  = Array.isArray(cap.fillLightMode) && cap.fillLightMode.length > 0;
    try {
      if (torchOk) {
        try {
          await track.applyConstraints({ advanced: [{ torch: enabled }] });
          return true;
        } catch {
          if (!enabled && fillOk) {
            const mode = cap.fillLightMode.includes('off') ? 'off' : cap.fillLightMode.includes('flash') ? 'flash' : null;
            if (mode) { await track.applyConstraints({ advanced: [{ fillLightMode: mode }] }); return true; }
          }
        }
      }
      if (fillOk) {
        const mode = enabled
          ? (cap.fillLightMode.includes('torch') ? 'torch' : cap.fillLightMode.includes('flash') ? 'flash' : null)
          : (cap.fillLightMode.includes('off') ? 'off' : null);
        if (mode) { await track.applyConstraints({ advanced: [{ fillLightMode: mode }] }); return true; }
      }
    } catch (e) { console.error('Torch toggle failed:', e); return false; }
  };

  const disableTorch = () => {
    const track = activeTrackRef.current;
    if (!track) { setIsTorchOn(false); localStorage.removeItem(TORCH_TRACK_STORE_KEY); return; }
    applyTorchToTrack(track, false).catch(() => {}).finally(() => {
      setIsTorchOn(false);
      localStorage.setItem(TORCH_TRACK_STORE_KEY, JSON.stringify({ id: track.id, torch: false }));
    });
  };

  const toggleTorch = async () => {
    let track = activeTrackRef.current;
    if (!track && typeof Quagga?.CameraAccess?.getActiveTrack === 'function') {
      track = Quagga.CameraAccess.getActiveTrack();
      activeTrackRef.current = track || null;
    }
    if (!track) { setSnackbarMessage('Kamera ist noch nicht bereit.'); return; }
    const next = !isTorchOn;
    const ok = await applyTorchToTrack(track, next);
    if (ok) {
      setIsTorchOn(next);
      localStorage.setItem(TORCH_TRACK_STORE_KEY, JSON.stringify({ id: track.id, torch: next, timestamp: Date.now() }));
    } else {
      setSnackbarMessage('Blitzmodus konnte nicht geändert werden.');
    }
  };

  // ── Kamera-Scan ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isScanning) {
      setPendingBarcode(''); setDetectionConfidence(0);
      lastDetectedCodeRef.current = ''; consecutiveDetections.current = 0;
      scanLockedRef.current = false;
      if (isTorchOn) disableTorch();
      setIsTorchAvailable(false); activeTrackRef.current = null;
    }
  }, [isScanning]);

  useEffect(() => {
    if (!isScanning) return;
    const videoTarget = document.querySelector('#video-container');
    if (!videoTarget) { setSnackbarMessage('Kamera konnte nicht gestartet werden.'); setIsScanning(false); return; }

    let torchPollTimer = null;
    const stopPoll = () => { if (torchPollTimer) { clearInterval(torchPollTimer); torchPollTimer = null; } };
    let trackEndedHandler = null;

    const captureTrack = () => {
      const track = Quagga.CameraAccess?.getActiveTrack?.();
      activeTrackRef.current = track || null;
      if (track && typeof track.getCapabilities === 'function') {
        const cap = track.getCapabilities();
        const hasTorch = cap?.torch === true ||
          (Array.isArray(cap.fillLightMode) && (cap.fillLightMode.includes('torch') || cap.fillLightMode.includes('flash')));
        setIsTorchAvailable(hasTorch);
        if (hasTorch) { stopPoll(); }
        else { setIsTorchOn(false); localStorage.removeItem(TORCH_TRACK_STORE_KEY); }
        if (hasTorch && track.addEventListener) {
          trackEndedHandler = () => { setIsTorchAvailable(false); setIsTorchOn(false); activeTrackRef.current = null; localStorage.removeItem(TORCH_TRACK_STORE_KEY); };
          track.addEventListener('ended', trackEndedHandler);
        }
      } else {
        setIsTorchAvailable(false); setIsTorchOn(false); localStorage.removeItem(TORCH_TRACK_STORE_KEY);
      }
    };

    const handleProcessed = (result) => {
      const ctx = Quagga?.canvas?.ctx?.overlay;
      const canvas = Quagga?.canvas?.dom?.overlay;
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (result?.boxes && Quagga.ImageDebug?.drawPath) {
        result.boxes.filter((b) => b !== result.box).forEach((b) =>
          Quagga.ImageDebug.drawPath(b, { x: 0, y: 1 }, ctx, { color: 'rgba(255,255,255,0.3)', lineWidth: 2 })
        );
      }
      if (result?.box && Quagga.ImageDebug?.drawPath)
        Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, ctx, { color: 'rgba(0,255,0,0.7)', lineWidth: 3 });
      if (result?.codeResult?.code) {
        ctx.font = '24px sans-serif'; ctx.fillStyle = 'rgba(0,255,0,0.8)';
        ctx.fillText(result.codeResult.code, 10, 40);
      }
    };

    const handleDetected = (data) => {
      if (scanLockedRef.current) return;
      const code = data?.codeResult?.code;
      if (!code || !isValidEAN(code)) return;
      const segs = Array.isArray(data?.codeResult?.decodedCodes) ? data.codeResult.decodedCodes : [];
      const errs = segs.map((s) => typeof s.error === 'number' ? Math.abs(s.error) : null).filter((e) => e !== null);
      const validSegs = errs.filter((e) => e < MAX_DECODE_ERROR).length;
      const avgErr = errs.length > 0 ? errs.reduce((a, e) => a + e, 0) / errs.length : 1;
      if (validSegs < MIN_VALID_SEGMENTS || avgErr > MAX_DECODE_ERROR) return;

      if (lastDetectedCodeRef.current === code) consecutiveDetections.current += 1;
      else { lastDetectedCodeRef.current = code; consecutiveDetections.current = 1; }

      // Noch nicht bei 3 – nur Zähler unten aktualisieren
      if (consecutiveDetections.current < DETECTION_CONFIRMATION_COUNT) {
        setPendingBarcode(code);
        setDetectionConfidence(consecutiveDetections.current);
        return;
      }

      // 3/3 erreicht – sperren, Counter ausblenden, Animations-Overlay zeigen
      scanLockedRef.current = true;
      setPendingBarcode('');
      setDetectionConfidence(0);
      setScanSuccess(true);

      const scannedCode = code;
      setTimeout(() => {
        setScanSuccess(false);
        stopScanning();
        // Suchfeld + Suche erst NACH der Animation
        setSearchText(scannedCode);
        setTimeout(() => { if (handleSearchRef.current) handleSearchRef.current(scannedCode); }, 150);
      }, 2200);
    };

    Quagga.init({
      inputStream: { type: 'LiveStream', target: videoTarget, constraints: { facingMode: 'environment' } },
      decoder: { readers: ['ean_reader', 'ean_8_reader'] },
      locate: true, debug: false,
      locator: { patchSize: 'medium', halfSample: true },
      numOfWorkers: typeof navigator !== 'undefined' && navigator.hardwareConcurrency ? Math.min(4, navigator.hardwareConcurrency) : 2,
      frequency: 10,
    }, (err) => {
      if (err) { console.error(err); setSnackbarMessage('Kamera konnte nicht gestartet werden.'); setIsScanning(false); return; }
      try { Quagga.start(); } catch (e) { console.error(e); setSnackbarMessage('Fehler beim Starten der Kamera.'); setIsScanning(false); }
    });

    setIsTorchOn(false); captureTrack();
    if (typeof window !== 'undefined') {
      torchPollTimer = window.setInterval(() => {
        if (!isScanning) { stopPoll(); return; }
        const prev = activeTrackRef.current; captureTrack();
        if (activeTrackRef.current && prev && activeTrackRef.current.id !== prev.id) setIsTorchOn(false);
      }, 400);
    }
    if (typeof Quagga.onProcessed === 'function') Quagga.onProcessed(handleProcessed);
    if (typeof Quagga.onDetected === 'function') Quagga.onDetected(handleDetected);

    return () => {
      try {
        if (typeof Quagga.offProcessed === 'function') Quagga.offProcessed(handleProcessed);
        if (typeof Quagga.offDetected === 'function') Quagga.offDetected(handleDetected);
        if (trackEndedHandler && activeTrackRef.current?.removeEventListener)
          activeTrackRef.current.removeEventListener('ended', trackEndedHandler);
        stopPoll();
        if (isTorchOn) disableTorch();
        Quagga.stop();
      } catch (e) { console.error(e); }
    };
  }, [isScanning]);

  // ── Filter ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    setFilteredLastAddedItems(lastAddedItems);
    setFilteredOwnAddedItems(ownAddedItems);
  }, [lastAddedItems, ownAddedItems]);

  const handleFilter = (query) => {
    setSearchText(query);
    if (tabIndex === 0) setFilteredLastAddedItems(lastAddedItems.filter((i) => i.product_name.toLowerCase().includes(query.toLowerCase())));
    else setFilteredOwnAddedItems(ownAddedItems.filter((i) => i.product_name.toLowerCase().includes(query.toLowerCase())));
  };

  // ── Mahlzeiten-übergreifende Suche in "Zuletzt verwendet" ──────────────────

  useEffect(() => {
    const q = searchText.trim();
    if (tabIndex !== 0 || !q) { setCrossMealItems([]); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/food/search-all?query=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const currentNames = new Set(filteredLastAddedItems.map((i) => i.product_name.toLowerCase()));
        const other = (data.last_added || []).filter(
          (item) => item.mealtime !== mealtime && !currentNames.has(item.product_name.toLowerCase())
        );
        setCrossMealItems(other);
      } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, tabIndex, mealtime, filteredLastAddedItems]);

  // ── Gerichte-Vorschläge während des Tippens ────────────────────────────────
  // Gerichte liegen in der eigenen DB und sind darum billig abfragbar — sie
  // erscheinen sofort, ohne dass die Suche abgeschickt werden muss.
  useEffect(() => {
    const q = searchText.trim();
    if (q.length < 2) { setDishSuggestions([]); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/dishes/search?query=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const dishes = await res.json();
        if (cancelled) return;
        setDishSuggestions(dishes.map((dish) => ({
          id: `dish-${dish.id}`, product_name: dish.name, source: 'dish',
          isDish: true, dishId: dish.id, components: dish.components,
          photo_url: dish.photo_url,
        })));
      } catch (e) { console.error(e); }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchText]);

  // ── Suche ──────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async (rawQuery) => {
    const query = (rawQuery || '').trim();
    setIsSearching(true); setFoodItems([]); setSelectedItem(null); setIsFromSearch(false);

    if (!query) { setSnackbarMessage('Bitte einen Suchbegriff eingeben.'); setIsSearching(false); return; }

    const possibleEANs = extractPossibleEANs(query);
    const isNumeric = /^\d+$/.test(query);
    const results = []; const seen = new Set(); let fallback = '';

    const addResult = (item, prepend = false) => {
      if (!item) return;
      const hasKcal = typeof item.kcal_complete === 'number' || typeof item.kcal === 'number' || typeof item.kcal_serving === 'number';
      if (!item.product_name || (!hasKcal && !item.isDish)) return;
      const key = `${item.source || 'generic'}-${item.id || item.code || item.product_name}`;
      if (seen.has(key)) return;
      seen.add(key);
      if (prepend) results.unshift(item); else results.push(item);
    };

    try {
      if (databaseSource === 'openfoodfacts') {
        const eansToQuery = possibleEANs.length > 0 ? possibleEANs :
          (isNumeric && query.length >= 8 && query.length <= 13 ? [query] : []);

        if (eansToQuery.length > 0) {
          const responses = await Promise.allSettled(
            eansToQuery.map((code) => fetch(`https://world.openfoodfacts.org/api/v0/product/${code}.json`).then((r) => r.json()))
          );
          responses.forEach((r) => {
            if (r.status !== 'fulfilled') return;
            const p = r.value?.product; if (!p) return;
            const n = p.nutriments || {};
            const u = p.product_quantity_unit === 'ml' || p.product_quantity_unit === 'l' ? 'ml' : 'g';
            addResult({ id: p.code || p.id || p._id, product_name: p.product_name || p.brands, kcal: toRoundedNumber(n['energy-kcal_100g']), kcal_serving: toRoundedNumber(n['energy-kcal_serving']), unit: u, source: 'openfoodfacts', code: p.code, protein_100g: toRoundedNumber(n['proteins_100g']), fat_100g: toRoundedNumber(n['fat_100g']), carbs_100g: toRoundedNumber(n['carbohydrates_100g']) });
          });
        }

        if (!isNumeric || results.length === 0) {
          const r = await fetch(`https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(query)}&search_simple=1&action=process&json=1&page_size=25`);
          if (r.ok) {
            const d = await r.json();
            if (Array.isArray(d?.products) && d.products.length > 0) {
              d.products.forEach((p) => {
                const n = p.nutriments || {};
                const u = p.product_quantity_unit === 'ml' || p.product_quantity_unit === 'l' ? 'ml' : 'g';
                addResult({ id: p.code || p.id || p._id, product_name: p.product_name || p.generic_name || p.brands, kcal: toRoundedNumber(n['energy-kcal_100g']), kcal_serving: toRoundedNumber(n['energy-kcal_serving']), unit: u, source: 'openfoodfacts', code: p.code, protein_100g: toRoundedNumber(n['proteins_100g']), fat_100g: toRoundedNumber(n['fat_100g']), carbs_100g: toRoundedNumber(n['carbohydrates_100g']) });
              });
            } else { fallback = 'Keine Treffer in OpenFoodFacts gefunden.'; }
          } else { fallback = 'OpenFoodFacts-Suche ist derzeit nicht erreichbar.'; }
        }
      }

      // Gerichte werden in jeder Datenquelle mitgesucht, damit sie direkt
      // in den Treffern auftauchen ("on the fly").
      {
        const token = localStorage.getItem('token');
        if (token) {
          const dishRes = await fetch(`/api/dishes/search?query=${encodeURIComponent(query)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (dishRes.ok) {
            const dishes = await dishRes.json();
            // Gerichte nach vorne, sie sind die konkretesten Treffer.
            dishes.reverse().forEach((dish) => addResult({
              id: `dish-${dish.id}`, product_name: dish.name, source: 'dish',
              isDish: true, dishId: dish.id, components: dish.components,
              photo_url: dish.photo_url,
            }, true));
          }
        }
      }

      if (databaseSource === 'nobnohungry') {
        const token = localStorage.getItem('token');
        if (!token) { setSnackbarMessage('Fehler: Nutzer nicht eingeloggt'); setIsSearching(false); return; }
        const terms = [query, ...possibleEANs.filter((e) => e !== query)];
        for (const term of terms) {
          const r = await fetch(`/api/community/search?query=${encodeURIComponent(term)}`, { headers: { Authorization: `Bearer ${token}` } });
          if (!r.ok) continue;
          const data = await r.json();
          if (!Array.isArray(data)) continue;
          data.forEach((item) => addResult({
            id: item.id, product_name: item.name, kcal: toRoundedNumber(item.kcal_per_100g),
            kcal_serving: toRoundedNumber(item.kcal_per_portion), unit: 'g', source: 'community',
            ean: item.ean, description: item.description, category: item.category, portion_size_g: item.portion_size_g,
            protein_100g: toRoundedNumber(item.protein_g), fat_100g: toRoundedNumber(item.fat_g), carbs_100g: toRoundedNumber(item.carbs_g),
          }));
        }
      }

      if (results.length === 0) setSnackbarMessage(fallback || 'Keine Treffer gefunden.');
      else { setFoodItems(results); setIsFromSearch(true); }
    } catch (e) {
      console.error(e); setSnackbarMessage('Ein unerwarteter Fehler ist aufgetreten.');
    }
    setIsSearching(false);
  }, [databaseSource]);

  useEffect(() => { handleSearchRef.current = handleSearch; }, [handleSearch]);

  const handleOpenOpenFoodFacts = useCallback(() => {
    const q = (searchText || '').trim();
    if (!q) { setSnackbarMessage('Bitte einen Suchbegriff eingeben.'); return; }
    window.open(`https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(q)}&search_simple=1&action=process`, '_blank', 'noopener,noreferrer');
  }, [searchText]);

  const startScanning = () => { if (!isScanning) setIsScanning(true); };
  const stopScanning  = () => setIsScanning(false);

  // ── Daten laden ─────────────────────────────────────────────────────────────
  const fetchLastAddedItems = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const r = await fetch('/api/food/last-added', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ mealtime }) });
    if (r.ok) setLastAddedItems(await r.json());
  };

  const fetchOwnAddedItems = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const r = await fetch(`/api/food/own-added?mealtime=${encodeURIComponent(mealtime)}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setOwnAddedItems(await r.json());
  };

  useEffect(() => { fetchLastAddedItems(); fetchOwnAddedItems(); }, []);

  // ── Lebensmittel hinzufügen ─────────────────────────────────────────────────
  const handleAddFood = async (calculatedCalories, isOwnFood = false) => {
    const token = localStorage.getItem('token');
    if (!token) { setSnackbarMessage('Fehler: Nutzer nicht eingeloggt'); return; }

    const actualCalories = typeof calculatedCalories === 'number' ? calculatedCalories : Math.round(totalCalories);
    const productName = selectedItem?.product_name || selectedItem?.name || selectedItem?.title || '';
    if (!productName) { setSnackbarMessage('Produktname fehlt.'); return; }

    const r = await fetch('/api/users/add-food-consumed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: productName, quantity, unit, mealtime, kcal: actualCalories, date: selectedDate, isOwnFood, protein_g: totalMacros.protein, fat_g: totalMacros.fat, carbs_g: totalMacros.carbs }),
    });

    if (r.ok) {
      triggerSuccess(productName, actualCalories);
      if (isOwnFood) fetchOwnAddedItems(); else fetchLastAddedItems();
    } else {
      setSnackbarMessage('Fehler beim Hinzufügen des Produkts.');
    }
  };

  const handleLastAddedFood = async (foodData) => {
    const token = localStorage.getItem('token');
    if (!token) { setSnackbarMessage('Fehler: Nutzer nicht eingeloggt'); setDialogOpen(false); return; }

    const productName = foodData?.product_name || selectedItem?.product_name || selectedItem?.name || '';
    const perHundred  = typeof foodData?.kcal_per_100g === 'number' ? foodData.kcal_per_100g :
      typeof selectedItem?.kcal === 'number' ? selectedItem.kcal :
      typeof selectedItem?.kcal_per_100g === 'number' ? selectedItem.kcal_per_100g : null;
    const perServing  = typeof foodData?.kcal_serving === 'number' ? foodData.kcal_serving :
      typeof selectedItem?.kcal_serving === 'number' ? selectedItem.kcal_serving : perHundred;
    const unitValue   = foodData?.unit || selectedItem?.unit || 'g';

    if (!productName || perHundred === null || perServing === null) {
      setSnackbarMessage('Dieses Produkt enthält unvollständige Nährwertdaten.');
      setDialogOpen(false); return;
    }

    const r = await fetch('/api/food/add-last-added-food', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ product_name: productName, kcal_per_100g: perHundred, unit: unitValue, mealtime, date: selectedDate, kcal_serving: perServing, protein_g: selectedItem?.protein_100g ?? 0, fat_g: selectedItem?.fat_100g ?? 0, carbs_g: selectedItem?.carbs_100g ?? 0 }),
    });

    if (r.ok) fetchLastAddedItems();
    else setSnackbarMessage('Fehler beim Hinzufügen des Produkts.');
    setDialogOpen(false);
  };

  // ── Löschen ─────────────────────────────────────────────────────────────────
  const entriesEqual = useCallback((a, b) => {
    if (!a || !b) return false;
    if (a.id && b.id) return String(a.id) === String(b.id);
    const n = (v) => (v === undefined || v === null || v === '') ? null : (Number.isNaN(Number(v)) ? null : Number(v));
    return a.product_name === b.product_name && Number(a.kcal_per_100g) === Number(b.kcal_per_100g) && a.unit === b.unit && n(a.kcal_serving) === n(b.kcal_serving);
  }, []);

  const handleDeleteLastAdded = async (item) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const params = new URLSearchParams({ mealtime });
    if (item.id) params.set('id', item.id);
    else { params.set('product_name', item.product_name); params.set('kcal_per_100g', String(item.kcal_per_100g)); params.set('unit', item.unit); params.set('kcal_serving', item.kcal_serving ?? ''); }
    const r = await fetch(`/api/food/delete-last-added?${params.toString()}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      setLastAddedItems((p) => p.filter((e) => !entriesEqual(e, item)));
      setFilteredLastAddedItems((p) => p.filter((e) => !entriesEqual(e, item)));
    }
  };

  const handleUpdateFood = async (id, newName, newKcal, newMealtime, newProtein, newFat, newCarbs) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const r = await fetch('/api/food/update-own-food', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ id, product_name: newName, kcal_complete: newKcal, mealtime: newMealtime, protein_g: newProtein ?? 0, fat_g: newFat ?? 0, carbs_g: newCarbs ?? 0 }),
    });
    if (r.ok) { fetchOwnAddedItems(); setEditDialogOpen(false); }
  };

  const handleDeleteFood = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const r = await fetch(`/api/food/delete-own-food?id=${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { fetchOwnAddedItems(); setEditDialogOpen(false); }
  };

  const handleDatabaseChange = (_, value) => {
    if (value) { setDatabaseSource(value); setSearchText(''); setFoodItems([]); setDishSuggestions([]); setSelectedItem(null); setIsFromSearch(false); }
  };

  // Gerichte erscheinen sowohl als Live-Vorschlag als auch im Suchergebnis.
  const renderDishRow = useCallback((item) => {
    const n = item.components?.length || 0;
    return (
      <ListRow
        key={item.id}
        title={`🍽️ ${item.product_name}`}
        subtitle={`Gericht · ${n} ${n === 1 ? 'Zutat' : 'Zutaten'}`}
        trailing={
          <Button
            variant="outlined" size="small"
            onClick={() => setDishDialogItem(item)}
            sx={{ borderRadius: radius.pill, fontWeight: 600, flexShrink: 0, borderWidth: 1.5 }}
          >
            Erfassen
          </Button>
        }
      />
    );
  }, []);

  // Keine Doppelung, wenn die abgeschickte Suche die Gerichte schon enthält.
  const visibleDishSuggestions = useMemo(() => {
    const already = new Set(foodItems.filter((i) => i.isDish).map((i) => i.id));
    return dishSuggestions.filter((d) => !already.has(d.id));
  }, [dishSuggestions, foodItems]);

  // Stabile Referenz: ohne useMemo bekäme DishLoggingDialog bei jedem Render
  // ein neues dish-Objekt und würde zurück auf die erste Zutat springen.
  const dishForLogging = useMemo(
    () => (dishDialogItem
      ? { id: dishDialogItem.dishId ?? dishDialogItem.id, name: dishDialogItem.product_name, components: dishDialogItem.components }
      : null),
    [dishDialogItem]
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Erfolgs-Overlay ── */}
      {showSuccess && (
        <SuccessOverlay entry={savedEntry} animate={isAnimEnabled()} />
      )}

      <Container maxWidth="sm" sx={{ pt: 3, pb: 14, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Header */}
        <PageHeader
          title={title}
          accentColor={accentColor}
          onBack={() => navigate('/dashboard')}
          dateChip={{
            label: `📅 ${formatDate(selectedDate)}`,
            isoDate: selectedDate,
            editable: true,
            onChange: (e) => e.target.value && setSelectedDate(e.target.value),
          }}
          sx={{ mb: 0 }}
        />

        {/* Datenbank-Auswahl */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { val: 'openfoodfacts', label: 'OpenFoodFacts' },
            { val: 'nobnohungry',   label: 'Eigene DB' },
            { val: 'dishes',        label: '🍽️ Gerichte' },
          ].map(({ val, label }) => (
            <Chip
              key={val}
              label={label}
              onClick={() => handleDatabaseChange(null, val)}
              color={databaseSource === val ? 'primary' : 'default'}
              variant={databaseSource === val ? 'filled' : 'outlined'}
              sx={{ fontWeight: databaseSource === val ? 700 : 400 }}
            />
          ))}
        </Box>

        {/* Suchzeile */}
        <Box sx={{
          display: 'flex', alignItems: 'center',
          border: 1, borderColor: 'divider', borderRadius: radius.xl,
          px: 1.5, gap: 0.5,
          '&:focus-within': { borderColor: 'primary.main' },
        }}>
          <TextField
            fullWidth
            placeholder="Artikel suchen oder EAN scannen"
            value={searchText}
            onChange={(e) => handleFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(e.target.value); } }}
            variant="standard"
            slotProps={{ input: { disableUnderline: true } }}
            sx={{ py: 0.5 }}
          />
          {isSearching
            ? <CircularProgress size={20} sx={{ mx: 1, flexShrink: 0 }} />
            : <IconButton onClick={() => handleSearch(searchText)} size="small"><Search fontSize="small" /></IconButton>
          }
          {databaseSource === 'openfoodfacts' && (
            <IconButton onClick={handleOpenOpenFoodFacts} size="small"><OpenInNew fontSize="small" /></IconButton>
          )}
          <IconButton onClick={startScanning} size="small"><CameraAlt fontSize="small" /></IconButton>
        </Box>

        {/* Barcode-Scanner */}
        {isScanning && (
          <Box sx={{
            position: 'fixed', top: '10%', left: '10%', width: '80%', height: '70%',
            bgcolor: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', zIndex: 1300, borderRadius: 4,
          }}>
            <Box id="video-container" sx={{ width: '100%', height: '100%', borderRadius: 4, overflow: 'hidden', position: 'relative', backgroundColor: 'black' }} />
            {/* Scan-Erfolgsoverlay */}
            {scanSuccess && (
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: 4,
                bgcolor: 'rgba(0, 0, 0, 0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2, pointerEvents: 'none',
                animation: 'scanBgIn 0.25s ease forwards',
                '@keyframes scanBgIn': {
                  from: { opacity: 0 },
                  to:   { opacity: 1 },
                },
              }}>
                <Box sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                  animation: 'scanPop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
                  '@keyframes scanPop': {
                    from: { transform: 'scale(0.5)', opacity: 0 },
                    to:   { transform: 'scale(1)',   opacity: 1 },
                  },
                }}>
                  <Box sx={{
                    width: 80, height: 80, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.celebration.gradientStart} 0%, ${theme.palette.celebration.gradientEnd} 100%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.celebration.gradientEnd, 0.5)}`,
                  }}>
                    <CheckCircle sx={{ color: 'common.white', fontSize: 44 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'common.white' }}>Erkannt!</Typography>
                </Box>
              </Box>
            )}
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Box sx={{ width: { xs: '85%', sm: '70%' }, height: { xs: '35%', sm: '30%' }, border: '2px solid rgba(144,238,144,0.7)', borderRadius: 2, position: 'relative', boxShadow: '0 0 12px rgba(144,238,144,0.6)' }}>
                <Box sx={{ position: 'absolute', top: '50%', left: 0, width: '100%', height: '2px', bgcolor: 'rgba(144,238,144,0.4)', transform: 'translateY(-50%)' }} />
                <Box sx={{ position: 'absolute', top: '12%', left: '8%', right: '8%', height: '65%', background: 'repeating-linear-gradient(90deg, rgba(144,238,144,0.35) 0px, rgba(144,238,144,0.35) 2px, transparent 2px, transparent 4px)', borderRadius: 1, opacity: 0.4 }} />
                <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', letterSpacing: '0.3em', color: 'rgba(144,238,144,0.8)', fontWeight: 500 }}>
                  4001234567890
                </Typography>
              </Box>
            </Box>
            <Box sx={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.65)', color: 'white', px: 2, py: 1, borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="subtitle2">Barcode bitte waagerecht im Rahmen ausrichten</Typography>
            </Box>
            <IconButton onClick={toggleTorch} disabled={!isTorchAvailable} sx={{ position: 'absolute', top: 8, left: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.6)', opacity: isTorchAvailable ? 1 : 0.5 }}>
              {isTorchOn ? <FlashOff /> : <FlashOn />}
            </IconButton>
            <IconButton onClick={stopScanning} sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.6)' }}>
              <Close />
            </IconButton>
            {pendingBarcode && !scanSuccess && (
              <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.65)', color: 'white', px: 2.5, py: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {detectionConfidence}/{DETECTION_CONFIRMATION_COUNT}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Gerichte-Vorschläge (live, während des Tippens) */}
        {visibleDishSuggestions.length > 0 && (
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1.2, mb: 1, display: 'block' }}>
              Gerichte ({visibleDishSuggestions.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {visibleDishSuggestions.map(renderDishRow)}
            </Box>
          </Box>
        )}

        {/* Suchergebnisse */}
        {foodItems.length > 0 && (
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1.2, mb: 1, display: 'block' }}>
              Suchergebnisse ({foodItems.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {foodItems.map((item, idx) => {
                if (item.isDish) return renderDishRow(item);

                const perHundred = typeof item.kcal === 'number' ? item.kcal : typeof item.kcal_per_100g === 'number' ? item.kcal_per_100g : null;
                const label = perHundred !== null ? `${perHundred} kcal / 100${item.unit || 'g'}` : null;
                return (
                  <ListRow
                    key={item.id || item.code || `${item.product_name}-${idx}`}
                    title={item.product_name}
                    subtitle={label}
                    trailing={
                      <Button
                        variant="outlined" size="small"
                        onClick={() => {
                          setSelectedItem(item); setQuantity(1);
                          setUnit(typeof item.kcal === 'number' ? '100' : typeof item.kcal_serving === 'number' ? 'portion' : '100');
                          setDialogOpen(true);
                        }}
                        sx={{ borderRadius: radius.pill, fontWeight: 600, flexShrink: 0, borderWidth: 1.5 }}
                      >
                        Erfassen
                      </Button>
                    }
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* Tab-Auswahl */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {['Zuletzt verwendet', 'Eigene Kombinationen'].map((label, i) => (
            <Chip
              key={i}
              label={label}
              onClick={() => setTabIndex(i)}
              color={tabIndex === i ? 'primary' : 'default'}
              variant={tabIndex === i ? 'filled' : 'outlined'}
              sx={{ fontWeight: tabIndex === i ? 700 : 400 }}
            />
          ))}
        </Box>

        {/* Tab 0: Zuletzt verwendet */}
        {tabIndex === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredLastAddedItems.length === 0 && crossMealItems.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                Noch keine Einträge vorhanden.
              </Typography>
            )}

            {filteredLastAddedItems.map((item, idx) => (
              <ListRow
                key={item.id || idx}
                title={item.product_name}
                subtitle={`${item.kcal_per_100g} kcal / 100${item.unit}`}
                trailing={
                  <>
                    <Button
                      variant="outlined" size="small"
                      onClick={() => { setSelectedItem(item); setQuantity(1); setUnit('100'); setIsFromSearch(false); setDialogOpen(true); }}
                      sx={{ borderRadius: radius.pill, fontWeight: 600, flexShrink: 0, borderWidth: 1.5 }}
                    >
                      Erfassen
                    </Button>
                    <IconButton size="small" onClick={() => handleDeleteLastAdded(item)} sx={{ color: 'text.disabled' }}>
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </>
                }
              />
            ))}

            {crossMealItems.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mt: 1, letterSpacing: 0.5 }}>
                  AUS ANDEREN MAHLZEITEN
                </Typography>
                {crossMealItems.map((item, idx) => (
                  <ListRow
                    key={`cross-${idx}`}
                    title={item.product_name}
                    subtitle={
                      <>
                        {item.kcal_per_100g} kcal / 100{item.unit}
                        {' · '}
                        <Box component="span" sx={{ fontStyle: 'italic' }}>
                          {MEAL_LABELS[item.mealtime] || item.mealtime}
                        </Box>
                      </>
                    }
                    trailing={
                      <Button
                        variant="outlined" size="small"
                        onClick={() => { setSelectedItem({ ...item, kcal: item.kcal_per_100g }); setQuantity(1); setUnit('100'); setIsFromSearch(false); setDialogOpen(true); }}
                        sx={{ borderRadius: radius.pill, fontWeight: 600, flexShrink: 0, borderWidth: 1.5 }}
                      >
                        Erfassen
                      </Button>
                    }
                    sx={{ opacity: 0.85 }}
                  />
                ))}
              </>
            )}
          </Box>
        )}

        {/* Tab 1: Eigene Kombinationen */}
        {tabIndex === 1 && (
          <>
            <Button
              variant="outlined" startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Neues Lebensmittel erstellen
            </Button>

            {filteredOwnAddedItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                Noch keine eigenen Lebensmittel vorhanden.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredOwnAddedItems.map((item, idx) => (
                  <ListRow
                    key={item.id || idx}
                    title={item.product_name}
                    subtitle={`${item.kcal_complete} kcal`}
                    trailing={
                      <>
                        <IconButton size="small" onClick={() => { setSelectedItem(item); setEditDialogOpen(true); }} sx={{ color: 'text.secondary' }}>
                          <Edit sx={{ fontSize: 18 }} />
                        </IconButton>
                        <Button
                          variant="outlined" size="small"
                          onClick={() => { setSelectedItem(item); setQuantity(1); setOwnDialogOpen(true); }}
                          sx={{ borderRadius: radius.pill, fontWeight: 600, flexShrink: 0, borderWidth: 1.5 }}
                        >
                          Erfassen
                        </Button>
                      </>
                    }
                  />
                ))}
              </Box>
            )}
          </>
        )}

      </Container>

      {/* ── Dialoge ── */}
      <QuantityFoodDialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        item={selectedItem}
        quantity={quantity}
        setQuantity={setQuantity}
        unit={unit}
        setUnit={setUnit}
        totalCalories={totalCalories}
        onAdd={() => {
          if (!selectedItem) { setSnackbarMessage('Kein Produkt ausgewählt.'); return; }
          if (isFromSearch) {
            handleLastAddedFood({ product_name: selectedItem.product_name, kcal_per_100g: selectedItem.kcal, unit: selectedItem.unit, mealtime, kcal_serving: selectedItem.kcal_serving });
            handleAddFood();
          } else {
            handleAddFood();
            setDialogOpen(false);
          }
        }}
      />

      <QuantityFoodDialog
        open={isOwnDialogOpen}
        onClose={() => setOwnDialogOpen(false)}
        item={selectedItem}
        quantity={quantity}
        setQuantity={setQuantity}
        onAdd={(cal) => {
          if (!selectedItem) { setSnackbarMessage('Kein Produkt ausgewählt.'); return; }
          handleAddFood(cal, true);
          setOwnDialogOpen(false);
        }}
      />

      <FoodFormDialog
        mode="edit"
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        item={selectedItem}
        onUpdate={(id, name, kcal, mt, prot, fat, carb) => handleUpdateFood(id, name, kcal, mt, prot, fat, carb)}
        onDelete={(id) => handleDeleteFood(id)}
      />

      <FoodFormDialog
        mode="add"
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onFoodAdded={() => fetchOwnAddedItems()}
      />

      <DishLoggingDialog
        open={Boolean(dishDialogItem)}
        onClose={() => setDishDialogItem(null)}
        dish={dishForLogging}
        mealtime={mealtime}
        date={selectedDate}
        onLogged={(kcal) => setConsumedToday((prev) => prev + kcal)}
      />
    </>
  );
};

export default MealPage;
